import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/product.service';
import { useToast } from '../components/common/Toast';
import { validateProductForm } from '../utils/productValidator';

export const useProductForm = (initialProduct = null, isEdit = false) => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // ✅ FIX: Initialize form data properly
  const getInitialFormData = (product) => ({
    category_id: product?.category_id || '',
    name: product?.name || '',
    slug: product?.slug || '',
    description: product?.description || '',
    price: product?.price || '',
    compare_price: product?.compare_price || '',
    cost_per_item: product?.cost_per_item || '',
    sku: product?.sku || '',
    barcode: product?.barcode || '',
    quantity: product?.quantity || 0,
    number_of_pieces: product?.number_of_pieces || 1,
    track_inventory: product?.track_inventory ?? true,
    weight: product?.weight || '',
    is_active: product?.is_active ?? true,
    is_featured: product?.is_featured ?? false,
    is_rental: product?.is_rental ?? false,
    whatsapp_number: product?.whatsapp_number || '',
    rent_per_month: product?.rent_per_month || '',
    tags: product?.tags || [],
    condition: product?.condition || 'new',
    meta_title: product?.meta_title || '',
    meta_description: product?.meta_description || '',
  });

  const [formData, setFormData] = useState(getInitialFormData(initialProduct));
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState(initialProduct?.images || []);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  // ✅ FIX: Update form data when initialProduct changes
  useEffect(() => {
    if (initialProduct) {
      setFormData(getInitialFormData(initialProduct));
      setExistingImages(initialProduct.images || []);
    }
  }, [initialProduct]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleImageAdd = (files) => {
    const fileArray = Array.from(files);
    const totalImages = existingImages.length + images.length + fileArray.length;
    
    if (totalImages > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    setImages(prev => [...prev, ...fileArray]);
  };

  const handleImageRemove = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleExistingImageRemove = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagAdd = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
  };

  const handleTagRemove = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const validate = () => {
    const validation = validateProductForm(formData);
    setErrors(validation.errors);
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => ({
      ...acc,
      [key]: true,
    }), {});
    setTouched(allTouched);

    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }

    // Check if at least one image exists (new or existing)
    if (!isEdit && images.length === 0) {
      toast.error('Please add at least one product image');
      return;
    }

    // For edit mode, check if we have at least one image (existing or new)
    if (isEdit && existingImages.length === 0 && images.length === 0) {
      toast.error('Product must have at least one image');
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (isEdit) {
        // ✅ FIX: For edit, send existing images as part of the data
        const updateData = {
          ...formData,
          existing_images: existingImages, // Include existing images that weren't removed
        };
        
        response = await productService.update(
          initialProduct.id,
          updateData,
          images
        );
        toast.success('Product updated successfully');
      } else {
        response = await productService.create(formData, images);
        toast.success('Product created successfully');
      }

      navigate('/seller/products');
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(getInitialFormData(null));
    setImages([]);
    setExistingImages([]);
    setErrors({});
    setTouched({});
  };

  return {
    formData,
    images,
    existingImages,
    errors,
    touched,
    loading,
    handleChange,
    handleBlur,
    handleImageAdd,
    handleImageRemove,
    handleExistingImageRemove,
    handleTagAdd,
    handleTagRemove,
    handleSubmit,
    validate,
    resetForm,
  };
};

export default useProductForm;